// Site-wide footer component for React app
import { useTranslation } from 'react-i18next';
import { useConsent } from '../../contexts/ConsentContext';

export function Footer() {
  const { t } = useTranslation('common');
  const { openModal } = useConsent();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="site-footer mt-16 border-t border-border bg-card">
      <div className="footer-container max-w-[1400px] mx-auto px-4 pt-12 pb-6">
        <div className="footer-main grid grid-cols-1 md:grid-cols-[1.5fr_2fr] gap-10 md:gap-16">
          {/* Brand section */}
          <div className="footer-brand max-w-[280px]">
            <a 
              href="/" 
              className="footer-logo inline-flex items-center gap-2 text-xl font-bold text-primary no-underline hover:opacity-90"
            >
              <span className="footer-logo-icon w-6 h-6">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="w-full h-full"
                >
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </span>
              <span>SimplaBooka</span>
            </a>
            <p className="footer-tagline mt-3 text-sm text-muted-foreground leading-relaxed">
              AI-powered book translation for language learners
            </p>
          </div>
          
          {/* Links grid */}
          <div className="footer-links-grid grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div className="footer-section">
              <h4 className="footer-section-title text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
                Product
              </h4>
              <ul className="footer-list list-none p-0 m-0 space-y-2.5">
                <li><a href="/" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">Home</a></li>
                <li><a href="/library" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">Library</a></li>
                <li><a href="/how-it-works/" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">How It Works</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-section-title text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
                {t('footer.legal', 'Legal')}
              </h4>
              <ul className="footer-list list-none p-0 m-0 space-y-2.5">
                <li><a href="/legal/terms/" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">{t('footer.terms', 'Terms of Service')}</a></li>
                <li><a href="/legal/privacy/" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">{t('footer.privacy', 'Privacy Policy')}</a></li>
                <li><a href="/legal/cookies/" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">{t('footer.cookies', 'Cookie Policy')}</a></li>
                <li>
                  <button 
                    onClick={openModal}
                    className="footer-manage-cookies text-sm text-muted-foreground hover:text-primary no-underline transition-colors bg-transparent border-none p-0 cursor-pointer"
                  >
                    {t('footer.manageCookies', 'Manage Cookies')}
                  </button>
                </li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-section-title text-sm font-semibold text-foreground mb-4 uppercase tracking-wider">
                Contact
              </h4>
              <ul className="footer-list list-none p-0 m-0 space-y-2.5">
                <li><a href="mailto:support@simplabooka.com" className="text-sm text-muted-foreground hover:text-primary no-underline transition-colors">support@simplabooka.com</a></li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Bottom section */}
        <div className="footer-bottom mt-10 pt-6 border-t border-border text-center">
          <p className="footer-copyright text-[0.8125rem] text-muted-foreground">
            © {currentYear} SimplaBooka. All rights reserved.
          </p>
          <p className="footer-developer mt-2 text-[0.75rem] text-muted-foreground">
            Developed by{' '}
            <a 
              href="https://www.asterios.no" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary no-underline transition-colors"
            >
              Asterios AS
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

